import { LegacyRef, forwardRef, useState } from 'react';
import * as React from 'react';
import { SelectInputOption } from '../../types/sharedTypes';
import { ArrowIcon } from './ArrowIcon';
import classNames from 'classnames';
import useOutsideClick from '../../hooks/useOutsideClick';

interface SelectInputProps {
  label?: string;
  disabled?: boolean;
  options: SelectInputOption[];
  onChange: (event: {
    target: { value: string | number; name: string };
  }) => void;
  value: string | number;
  name: string;
  placeholder?: string;
}

const CustomSelectInput = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (props: SelectInputProps, _ref: LegacyRef<HTMLDivElement>) => {
    const {
      label,
      disabled = false,
      options,
      value,
      onChange,
      name,
      placeholder = 'Select an option',
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isMouseOutside, setIsMouseOutside] = useState(true);

    const optionsRef = useOutsideClick(() => setIsOpen(false));

    const handleMouseEnter = () => {
      setIsMouseOutside(false);
    };

    const handleMouseLeave = () => {
      setIsMouseOutside(true);
    };
    const getLabelByValue = () => {
      return (
        options.find((option) => option.value === value)?.label ?? placeholder
      );
    };

    const toggleSelect = () => {
      if (!disabled) setIsOpen(!isOpen);
      if (isOpen) setIsMouseOutside(true);
    };

    const handleOptionClick = (option: string | number) => {
      if (onChange) onChange({ target: { value: option, name } });
      setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!disabled) {
        if ((e.key === 'ArrowDown' || e.key === 'Enter') && !isOpen) {
          e.preventDefault();
          setIsOpen(true);
        } else if (e.key === 'Escape') {
          setIsOpen(false);
        }
      }
    };

    return (
      <div
        data-test-id='select-input-containter'
        className='w-full mb-5 relative bg-white'
      >
        {label && (
          <label
            htmlFor={name}
            className='text-sm font-medium text-dark-header'
          >
            {label}
          </label>
        )}
        <div className='w-full relative'>
          <div
            id={name}
            onClick={toggleSelect}
            onKeyDown={handleKeyDown}
            ref={optionsRef}
            role='combobox'
            data-test-id={label}
            aria-expanded={isOpen}
            aria-haspopup='listbox'
            tabIndex={disabled ? -1 : 0}
            className='w-full relative'
          >
            <div
              className={classNames(
                `w-full flex items-center justify-between px-[10px] h-[44px] border rounded
               `,
                { 'bg-gray-100 cursor-not-allowed': disabled },
                { 'border-gray-200 cursor-pointer': !disabled },
              )}
            >
              <p className='text-sm leading-5 text-gray-600'>
                {getLabelByValue()}
              </p>
              <ArrowIcon />
            </div>
            {isOpen && (
              <ul
                role='listbox'
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={classNames(
                  `list-none p-0 m-0 absolute top-full left-0 w-full border border-gray-200  z-10 mt-1 rounded max-h-80 overflow-x-auto bg-white`,
                  { 'bg-gray-200': disabled },
                )}
              >
                {options.map((option) => (
                  <li
                    key={option.value}
                    onClick={() => handleOptionClick(option.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleOptionClick(option.value);
                        setIsOpen(false);
                      }
                    }}
                    className={classNames(
                      `p-[10px] cursor-pointer text-base
                      hover:bg-gray-200 hover:shadow-md`,
                      {
                        'bg-gray-200 border-r-2 border-purple-600 rounded-tl rounded-bl':
                          option.value === value && !!isMouseOutside,
                      },
                    )}
                    role='option'
                    tabIndex={0}
                  >
                    {option.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  },
);
export default CustomSelectInput;
